import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { withRouter } from "react-router-dom";
import { Box, Container, Icon } from "@material-ui/core";
import styled from "styled-components";
import {
  ErrorOutline as ErrorOutlineIcon,
  Inbox as RefoIcon,
  Description as DescriptionIcon,
  DateRange as DateRangeIcon,
  TableChartOutlined as TableIcon,
  ChildCare as ChildCareIcon,
  Class,
  Assessment,
  PlaylistAddCheck,
  Mic as MicIcon,
  Layers as LayersIcon,
  ShowChart as ShowChartIcon
} from "@material-ui/icons";
import { useTranslation } from "react-i18next";
import SquareButton from "../../../waybee-ui/SquareButton";
import NewFeatureBadge from "../../../components/NewFeatureBadge";
import { Can } from "../../../acl/can";
import MenuService from "../../../services/MenuService";
import {
  buildMenuTarget,
  DIARY_CLASSES_MENU
} from "../../../utils/dynamicMenu";

function materialIcon(name) {
  const glyph = name || "list_alt";
  return React.forwardRef(function MenuMaterialIcon(props, ref) {
    return (
      <Icon {...props} ref={ref}>
        {glyph}
      </Icon>
    );
  });
}

const TileWrap = styled.div`
  position: relative;
  display: inline-block;
`;

const GroupActions = props => {
  const { t } = useTranslation();
  const { history, match } = props;
  const classId = match && match.params ? match.params.id : "";
  const [dynamicMenus, setDynamicMenus] = useState([]);
  const redirectTo = route => {
    const { pathname } = history.location;
    history.push(`${pathname}${route}`);
  };

  useEffect(() => {
    if (!classId) return undefined;
    let active = true;
    MenuService.list(DIARY_CLASSES_MENU, classId)
      .then(menus => {
        if (active) setDynamicMenus(menus || []);
      })
      .catch(() => {
        if (active) setDynamicMenus([]);
      });
    return () => {
      active = false;
    };
  }, [classId]);

  return (
    <Box mt={2}>
      <Container maxWidth="xg">
        <Box display="flex" justifyContent="center" flexWrap="wrap">
          <Can I="use module" a="reports">
            <Box px={2} pt={2}>
              <SquareButton
                icon={Assessment}
                color="primary"
                onClick={() => redirectTo("/reports")}
              >
                {t("classes.actions.reports")}
              </SquareButton>
            </Box>
          </Can>
          <Can I="use module" a="diaryOccurrences">
            <Box px={2} pt={2}>
              <SquareButton
                icon={ErrorOutlineIcon}
                color="primary"
                onClick={() => redirectTo("/occurrences")}
              >
                {t("classes.actions.occurrences")}
              </SquareButton>
            </Box>
          </Can>
          <Can I="use module" a="diaryOccurrenceAdvanced">
            <Box px={2} pt={2}>
              <SquareButton
                icon={LayersIcon}
                color="primary"
                onClick={() => redirectTo("/occurrencesAdvanced")}
              >
                {t("classes.actions.occurrenceAdvanced")}
              </SquareButton>
            </Box>
          </Can>
          <Can I="use module" a="refo">
            <Box px={2} pt={2}>
              <SquareButton
                icon={RefoIcon}
                color="primary"
                onClick={() => redirectTo("/refo")}
              >
                {t("classes.actions.refo")}
              </SquareButton>
            </Box>
          </Can>
          <Can I="use module" a="classStages">
            <Box px={2} pt={2}>
              <SquareButton
                icon={ShowChartIcon}
                color="primary"
                onClick={() => redirectTo("/stages")}
              >
                {t("classes.actions.stages")}
              </SquareButton>
            </Box>
          </Can>

          <Can I="use module" a="scoresV1">
            <Box px={2} pt={2}>
              <SquareButton
                icon={DescriptionIcon}
                color="primary"
                onClick={() => redirectTo("/grades")}
              >
                {t("classes.actions.gradesV1")}
              </SquareButton>
            </Box>
          </Can>
          <Can I="use module" a="scoresV2">
            <Box px={2} pt={2}>
              <SquareButton
                icon={DescriptionIcon}
                color="primary"
                onClick={() => redirectTo("/gradesV2")}
              >
                {t("classes.actions.gradesV2")}
              </SquareButton>
            </Box>
          </Can>
          <Can I="use module" a="gridFrequency">
            <Box px={2} pt={2}>
              <SquareButton
                icon={PlaylistAddCheck}
                color="primary"
                onClick={() => redirectTo("/frequency")}
              >
                {t("classes.actions.frequency")}
              </SquareButton>
            </Box>
          </Can>
          <Can I="use module" a="individualReport">
            <Box px={2} pt={2}>
              <SquareButton
                icon={TableIcon}
                color="primary"
                onClick={() => redirectTo("/individualReport/new")}
              >
                {t("classes.actions.individualReport")}
              </SquareButton>
            </Box>
          </Can>
          <Can I="use module" a="dailySummary">
            <Box px={2} pt={2}>
              <SquareButton
                icon={ChildCareIcon}
                color="primary"
                onClick={() => redirectTo("/dailySummary")}
              >
                {t("classes.actions.dailySummary")}
              </SquareButton>
            </Box>
          </Can>
          <Can I="use module" a="classPlan">
            <Box px={2} pt={2}>
              <SquareButton
                icon={Class}
                color="primary"
                onClick={() => redirectTo("/plan")}
              >
                {t("classes.actions.classPlan")}
              </SquareButton>
            </Box>
          </Can>
          <Can I="use module" a="schedule">
            <Box px={2} pt={2}>
              <SquareButton
                icon={DateRangeIcon}
                color="primary"
                onClick={() => redirectTo("/schedule")}
              >
                {t("classes.actions.schedule")}
              </SquareButton>
            </Box>
          </Can>
          <Can I="use module" a="smartMinutes">
            <Box px={2} pt={2}>
              <TileWrap>
                <NewFeatureBadge floating top="6px" right="10px" />
                <SquareButton
                  icon={MicIcon}
                  color="primary"
                  onClick={() => redirectTo("/ata-inteligente")}
                >
                  {t("classes.actions.smartMinutes")}
                </SquareButton>
              </TileWrap>
            </Box>
          </Can>
          {dynamicMenus.map(menu => (
            <Box key={menu.id} px={2} pt={2}>
              <SquareButton
                icon={materialIcon(menu.icon)}
                color="primary"
                onClick={() => history.push(buildMenuTarget(menu, { classId }))}
              >
                {menu.title}
              </SquareButton>
            </Box>
          ))}
        </Box>
      </Container>
    </Box>
  );
};

GroupActions.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  history: PropTypes.object.isRequired,
  match: PropTypes.shape({
    params: PropTypes.shape({
      id: PropTypes.string
    })
  }).isRequired
};

export default withRouter(GroupActions);
